/* eslint-disable no-duplicate-imports */
/* eslint-disable react/jsx-props-no-spreading */
import * as ReactDOMClient from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { type FC, useCallback, useContext, useRef, useState, useEffect } from 'react';
import { useEventCalendarQuery, type EventCalendarQuery } from 'cms/cms';
import type { EventCalendarData } from 'cms/resolvers/eventCalendarResolver';
import { resolveEventCalendar } from 'cms/resolvers/eventCalendarResolver';
import EventCalendarProvider from './EventCalendarProvider';
import EventCalendarContext, { type EventCalendarContextProps, type EventCalendarProps } from './EventCalendarContext';
import { getWhereValue, type WhereItem } from '3-components/ListingApp/ListingFilters/ListingFilterHelpers';
import { DEFAULT_FETCH_PARAMS, OPTMIZELY_GRAPHQL_BASE_URL } from 'cms/constants';
import { ListingError, ListingLoader } from '3-components/ListingApp/ListingUI/ListingUI';
import EventNavBar from './EventNavBar/EventNavBar';
import EventsWrapper from './EventsWrapper/EventsWrapper';
import EventFilters from './EventFilters/EventFilters';
import { getMonthsWithValidEvents, parseYMDToLocalDate } from './EventCalendarHelpers';
import { filtersToQueryString, queryStringToFilters, validateFilters } from './QuerystringHelpers';

const EventCalendar: FC = () => {
    const { cms, hideFilters, filters, dataTypes, experienceBrands: brands } = useContext(EventCalendarContext) as EventCalendarContextProps;

    // Get initial filters from URL or props (URL takes precedence)
    const getInitialFilters = useCallback((): Record<string, string[]> => {
        // Check URL params first
        const urlParams = queryStringToFilters(window.location.search.slice(1));

        if (Object.keys(urlParams).length > 0) {
            return urlParams;
        }

        // Extract default active filters from the filters prop
        if (filters && filters.length > 0) {
            const result = filters.reduce<Record<string, string[]>>((acc, filter) => {
                if (filter.activeDefaultValues && filter.activeDefaultValues.length > 0) {
                    // Use the exact filter name from the filters prop (no case transformation)
                    acc[filter.name] = filter.activeDefaultValues.map(item => {
                        return item.name;
                    });
                }
                return acc;
            }, {});

            return result;
        }

        return {};
    }, [filters]);

    // eslint-disable-next-line react/hook-use-state
    const [activeFilters, updateActiveFilters] = useState<Record<string, string[]>>(getInitialFilters);

    // Always validate filters before updating state
    const setActiveFilters = useCallback((nextFilters: Record<string, string[]>) => {
        // Use filters prop for allowed filters/values
        const validated = validateFilters(nextFilters, filters || []);
        // Deduplicate values for each filter
        const deduped: Record<string, string[]> = {};
        Object.entries(validated).forEach(([key, arr]) => {
            deduped[key] = Array.from(new Set(arr));
        });
        updateActiveFilters(deduped);
    }, [filters]);
    const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);

    // Update active filters when filters prop changes
    useEffect(() => {
        const newActiveFilters = getInitialFilters();
        setActiveFilters(newActiveFilters);
    }, [getInitialFilters, setActiveFilters]);

    // Update URL when filters change
    useEffect(() => {
        const queryString = filtersToQueryString(activeFilters);
        // Create a URL object for the current location
        const currentUrl = new URL(window.location.href);
        // Set the search params of the current URL to the new query string
        currentUrl.search = queryString ? `?${queryString}` : '';
        window.history.replaceState({}, '', currentUrl.toString());
    }, [activeFilters]);

    // Listen for browser back/forward navigation
    useEffect(() => {
        const handlePopState = (): void => {
            const urlFilters = queryStringToFilters(window.location.search.slice(1));
            setActiveFilters(urlFilters);
        };

        window.addEventListener('popstate', handlePopState);
        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [setActiveFilters]);

    const GRAPHQL_BASE_URL = cms.apiUrl || OPTMIZELY_GRAPHQL_BASE_URL;

    const scrollToEl = useRef<HTMLDivElement>(null);

    const whereItems: WhereItem[] = [];
    if (cms.siteId) {
        whereItems.push({
            type: 'siteId',
            field: 'SiteId',
            value: [cms.siteId],
        });
    }

    const where = getWhereValue(whereItems);

    // eslint-disable-next-line object-curly-newline
    const { data, error, isLoading, isError } = useEventCalendarQuery({ endpoint: `${GRAPHQL_BASE_URL}${cms.apiKey}`, fetchParams: DEFAULT_FETCH_PARAMS }, {
        where,
    // eslint-disable-next-line object-curly-newline
    }, {
        queryKey: ['event-calendar'],
        keepPreviousData: true,
        staleTime: 1000 * 60 * 5,
        select: useCallback((result: EventCalendarQuery) => {
            return resolveEventCalendar(result, dataTypes, brands);
        }, [dataTypes, brands]),
    });

    const dataToUse = data;

    // Filter events based on active filters with field-specific matching
    const filteredData = dataToUse ? {
        ...dataToUse,
        eventCalendarCards: dataToUse.eventCalendarCards.filter((event: { type?: string; title?: string; category?: string; details?: string; team1?: string; team2?: string; calendarTags?: string[] }) => {
            // If no filters are active, show all events
            if (Object.keys(activeFilters).length === 0) {
                return true;
            }

            return Object.entries(activeFilters).every(([filterField, filterValues]) => {
                if (filterValues.length === 0) {
                    return true;
                }

                // Find the filter configuration to check if it's a custom filter
                const filterConfig = filters?.find(f => {
                    return f.name === filterField;
                });
                const isCustomFilter = filterConfig?.isCustomFilter || false;

                let eventValueToCheck = '';

                if (isCustomFilter) {
                    // For custom filters, check against CalendarTags
                    const calendarTags = event.calendarTags || [];
                    eventValueToCheck = calendarTags.join(' ').toLowerCase();
                } else {
                    // Normalize filter field to lowercase for case-insensitive matching
                    const normalizedFilterField = filterField.toLowerCase();

                    switch (normalizedFilterField) {
                    case 'event type':
                        eventValueToCheck = event.type || '';
                        break;
                    case 'event category':
                        eventValueToCheck = event.category || '';
                        break;
                    case 'afl teams':
                    case 'afl team':
                    case 'bbl teams':
                    case 'bbl team':
                        eventValueToCheck = [event.team1, event.team2].filter(Boolean).join(' ');
                        break;
                    default:
                        eventValueToCheck = '';
                        break;
                    }
                }

                // Check if any of the selected filter values match the event property
                const matchResult = filterValues.some(selectedValue => {
                    const selectedValueLower = selectedValue.toLowerCase();
                    const eventValueLower = eventValueToCheck.toLowerCase();

                    // Direct exact match - filter value must appear in event content
                    const matches = eventValueLower.includes(selectedValueLower);

                    return matches;
                });

                return matchResult;
            });
        }),
    } : dataToUse;

    if (isLoading) {
        return (
            <div className="calendar-app">
                <ListingLoader loadingMessage={'Loading'} />
            </div>
        );
    }

    if (isError || !dataToUse) {
        console.error(error);

        return (
            <ListingError errorMessage={'Error'} />
        );
    }

    return (
        <div className="calendar-app loaded" ref={scrollToEl}>
            <div className="container">
                {/* Always show navigation and content area */}
                {(filteredData || dataToUse) && (
                    <>
                        <EventNavBar data={(filteredData || dataToUse) as EventCalendarData} />
                        <div className="event-content">
                            {!hideFilters && (
                                <EventFilters
                                    isOpen={isMobileFiltersOpen}
                                    onClose={(): void => {
                                        setIsMobileFiltersOpen(false);
                                    }}
                                    onToggle={(): void => {
                                        setIsMobileFiltersOpen(true);
                                    }}
                                    calendarRef={scrollToEl}
                                    filters={(filters || []).filter(filter => {
                                        return !filter.hideFilter;
                                    })}
                                    activeFilters={activeFilters}
                                    onFiltersChange={setActiveFilters}
                                    totalResults={((): number => {
                                        const dataForCount = (filteredData || dataToUse);
                                        if (!dataForCount) {
                                            return 0;
                                        }

                                        return getMonthsWithValidEvents(dataForCount).reduce((count, month) => {
                                            const today = new Date();
                                            const validEvents = dataForCount.eventCalendarCards.filter(event => {
                                                const eventDate = parseYMDToLocalDate(event.date);
                                                const eventYear = eventDate.getFullYear();
                                                const eventMonth = eventDate.getMonth();
                                                const eventDay = eventDate.getDate();

                                                // Check if event is in this month
                                                if (eventYear !== month.year || eventMonth !== month.monthIndex) {
                                                    return false;
                                                }

                                                // Apply same date filtering as EventCalendarMonth
                                                const isCurrentMonth = month.monthIndex === today.getMonth() && month.year === today.getFullYear();
                                                const isPastToday = isCurrentMonth && eventDay < today.getDate();

                                                return !isPastToday;
                                            });
                                            return count + validEvents.length;
                                        }, 0);
                                    })()}
                                />
                            )}
                            <EventsWrapper
                                data={(filteredData || dataToUse) as EventCalendarData}
                                hasNoResults={!!(
                                    (filteredData && Object.keys(activeFilters).length > 0 && getMonthsWithValidEvents(filteredData).length === 0) ||
                                    (dataToUse && getMonthsWithValidEvents(dataToUse).length === 0)
                                )}
                                onResetFilters={(): void => {
                                    setActiveFilters({});
                                    // Clear URL params when resetting filters
                                    window.history.replaceState({}, '', window.location.pathname);
                                }}
                                hideFilters={hideFilters}
                                hasActiveFilters={Object.keys(activeFilters).length > 0}
                            />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

const queryClient = new QueryClient();

interface EventCalendarInitProps {
    el: HTMLDivElement;
    props: EventCalendarProps;
}

const EventCalendarInit = ({ el, props }: EventCalendarInitProps): void => {
    const root = ReactDOMClient.createRoot(el);

    root.render(
        <QueryClientProvider client={queryClient}>
            <EventCalendarProvider {...props}>
                <EventCalendar />
            </EventCalendarProvider>
        </QueryClientProvider>
    );
};

export default EventCalendarInit;
